/**
 * Meta Pixel + Conversions API tracking utility.
 * Client-side: fires fbq() events for the pixel.
 * Server-side: sends events to /api/tracking/event for CAPI deduplication.
 */
import axios from "axios";
import { trackingAllowed } from "@/utils/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const META_PIXEL_ID = "2082360579278723";
const FBCLID_KEY = "pos_fbclid";

/* ---------- consent-gated init ----------
 * The pixel is init'd here (not in index.html) so an EEA/UK visitor's _fbp
 * cookie is never set before consent. consent.js calls this once the visitor is
 * "open" (non-EEA/UK, or after they accept). Idempotent; the fbq shim in
 * index.html queues any track() calls made before init and flushes them here. */
let _pixelInited = false;
export function initMetaPixel() {
  if (_pixelInited) return;
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("init", META_PIXEL_ID);
    _pixelInited = true;
  } catch { /* pixel blocked / not loaded */ }
}
const FBC_KEY = "pos_fbc";
const FBC_CREATED_AT_KEY = "pos_fbc_created_at";
const LANDING_PAGE_KEY = "pos_landing_page";
const REFERRER_KEY = "pos_referrer";

/* ---------- deduplication cache ---------- */
const firedEvents = new Map(); // Map<eventKey, timestamp>
const DEDUP_WINDOW_MS = 5000; // 5 seconds deduplication window

function getEventKey(eventName, customData) {
  return `${eventName}_${JSON.stringify(customData)}`;
}

function shouldFireEvent(eventName, customData) {
  const key = getEventKey(eventName, customData);
  const lastFired = firedEvents.get(key);
  const now = Date.now();
  
  if (lastFired && (now - lastFired) < DEDUP_WINDOW_MS) {
    console.log(`[Meta Pixel] Skipping duplicate event: ${eventName}`);
    return false;
  }
  
  firedEvents.set(key, now);
  
  // Clean up old entries periodically
  if (firedEvents.size > 50) {
    for (const [k, v] of firedEvents.entries()) {
      if (now - v > DEDUP_WINDOW_MS * 2) {
        firedEvents.delete(k);
      }
    }
  }
  
  return true;
}

/* ---------- helpers ---------- */

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : "";
}

function generateEventId() {
  return "eid_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
}

function getAuthHeaders() {
  const headers = {};
  const token = localStorage.getItem("pos_token");
  const sessionId = localStorage.getItem("pos_session");
  if (token) headers.Authorization = `Bearer ${token}`;
  if (sessionId) headers["X-Session-ID"] = sessionId;
  return headers;
}

function getCurrentFbclid() {
  try {
    return new URLSearchParams(window.location.search).get("fbclid") || "";
  } catch {
    return "";
  }
}

function getStorageItem(key) {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function setStorageItem(key, value) {
  if (!value) return;
  try {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  } catch {
    // storage may be unavailable in private/browser-restricted contexts
  }
}

export function captureMetaAttribution() {
  if (typeof window === "undefined") return;

  const fbclid = getCurrentFbclid();
  if (fbclid) {
    setStorageItem(FBCLID_KEY, fbclid);
    if (!getStorageItem(FBC_CREATED_AT_KEY)) {
      setStorageItem(FBC_CREATED_AT_KEY, String(Date.now()));
    }
  }

  const cookieFbc = getCookie("_fbc");
  if (cookieFbc) {
    setStorageItem(FBC_KEY, cookieFbc);
  }

  if (!getStorageItem(LANDING_PAGE_KEY)) {
    setStorageItem(LANDING_PAGE_KEY, window.location.href);
  }
  if (typeof document !== "undefined" && document.referrer && !getStorageItem(REFERRER_KEY)) {
    setStorageItem(REFERRER_KEY, document.referrer);
  }
}

function getStoredFbclid() {
  return getCurrentFbclid() || getStorageItem(FBCLID_KEY);
}

function getFbc() {
  const cookieFbc = getCookie("_fbc");
  if (cookieFbc) {
    setStorageItem(FBC_KEY, cookieFbc);
    return cookieFbc;
  }

  const storedFbc = getStorageItem(FBC_KEY);
  if (storedFbc) return storedFbc;

  const fbclid = getStoredFbclid();
  if (!fbclid) return "";

  const createdAt = getStorageItem(FBC_CREATED_AT_KEY) || String(Date.now());
  setStorageItem(FBC_CREATED_AT_KEY, createdAt);
  const fbc = `fb.1.${createdAt}.${fbclid}`;
  setStorageItem(FBC_KEY, fbc);
  return fbc;
}

export function getMetaAttributionPayload() {
  captureMetaAttribution();
  return {
    fbp: getCookie("_fbp"),
    fbc: getFbc(),
    fbclid: getStoredFbclid(),
    event_source_url: window.location.href,
    landing_page: getStorageItem(LANDING_PAGE_KEY) || window.location.href,
    referrer: getStorageItem(REFERRER_KEY) || document.referrer || "",
  };
}

/* ---------- core track function ---------- */

/**
 * Track a Meta event on both client (fbq) and server (CAPI).
 *
 * @param {string} eventName  - Standard or custom Meta event name. Pass
 *   { customEvent: true } for browser Pixel custom events.
 * @param {object} customData - Additional event data (value, currency, content_name, etc.)
 * @param {object} options    - { serverOnly: bool, clientOnly: bool, skipDedup: bool, customEvent: bool }
 */
export function trackEvent(eventName, customData = {}, options = {}) {
  // GDPR: for an EEA/UK visitor who hasn't consented, fire NOTHING — not the
  // client pixel and not the server CAPI (which carries fbp/fbc). Non-EEA/UK
  // visitors are never blocked, so this is a no-op for India/US.
  if (!trackingAllowed()) return null;

  captureMetaAttribution();

  // Skip PageView from deduplication (it's expected to fire on navigation)
  if (!options.skipDedup && eventName !== "PageView" && !shouldFireEvent(eventName, customData)) {
    return null; // Duplicate event, skip
  }
  
  const eventId = options.eventId || generateEventId();

  // --- Client-side pixel ---
  if (!options.serverOnly && typeof window.fbq === "function") {
    try {
      window.fbq(options.customEvent ? "trackCustom" : "track", eventName, customData, { eventID: eventId });
    } catch (e) {
      // pixel not loaded or blocked
    }
  }

  // --- Server-side CAPI ---
  if (!options.clientOnly) {
    const attribution = getMetaAttributionPayload();
    const payload = {
      event_name: eventName,
      event_id: eventId,
      event_source_url: attribution.event_source_url,
      fbp: attribution.fbp,
      fbc: attribution.fbc,
      fbclid: attribution.fbclid,
      landing_page: attribution.landing_page,
      referrer: attribution.referrer,
      custom_data: customData,
    };
    axios
      .post(`${API}/tracking/event`, payload, { headers: getAuthHeaders() })
      .catch(() => {
        // silently fail — tracking should never break the app
      });
  }

  return eventId;
}

/* ---------- convenience wrappers ---------- */

/** Track PageView — called on route change */
export function trackPageView() {
  trackEvent("PageView", { page: window.location.pathname });
}

/** Track registration */
export function trackRegistration(email) {
  trackEvent("CompleteRegistration", {
    content_name: "PlacementOS Registration",
    status: "complete",
  });
}

/** Track a Lead event with optional funnel metadata */
export function trackLead(contentName = "Resume Upload", customData = {}) {
  return trackEvent("Lead", {
    content_name: contentName,
    ...customData,
  });
}

/** Track a custom Meta event on both browser Pixel and CAPI with one event ID. */
export function trackCustomEvent(eventName, customData = {}, options = {}) {
  return trackEvent(eventName, customData, { ...options, customEvent: true });
}

/** A deterministic conversion signal for a successful resume upload. */
export function trackResumeUploadComplete(customData = {}) {
  return trackCustomEvent("ResumeUploadComplete", {
    content_name: "Free ATS Score Resume Upload",
    event_stage: "resume_upload_success",
    funnel: "resume_analysis_frontend",
    ...customData,
  });
}

/** Track InitiateCheckout */
export function trackInitiateCheckout(value, currency = "INR", contentName = "Season Pass") {
  trackEvent("InitiateCheckout", {
    value,
    currency,
    content_name: contentName,
  });
}

/** Track Purchase */
export function trackPurchase(value, currency = "INR", contentName = "Season Pass", options = {}) {
  trackEvent("Purchase", {
    value,
    currency,
    content_name: contentName,
    content_type: "product",
  }, options);
}

/** Track ViewContent */
export function trackViewContent(contentName, contentCategory = "") {
  trackEvent("ViewContent", {
    content_name: contentName,
    content_category: contentCategory,
  });
}
